import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import { Checkbox } from '@savvy-kit/components/checkbox';
import IconButton from '@savvy-kit/components/icon-button';
import { TableCell } from '@savvy-kit/components/table';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Ellipsis } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';
import { formatDollarAmount } from 'src/utils/formatting';

import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import { castToTaxEnrollmentType, formatTaxEnrollment } from '../tax-utils';
import { getColumnSizing } from './tax-overview-table-columns';
import { TaxStatusBadge } from './tax-status-badge';

export const TaxOverviewTableBodyRowFragment = graphql(/* GraphQL */ `
  fragment TaxOverviewTableBodyRow on TaxEnrollmentsTableRow {
    rowId
    enrollmentType
    taxYear
    price
    enrollmentStatus
    transcriptStatus
    federalReturnStatus
    aprilAppStatus
    clientName
    householdName
    salesforceId
    stateFilings {
      stateCode
      status
    }
  }
`);

type Props = {
  readonly rowFrag: FragmentType<typeof TaxOverviewTableBodyRowFragment> | undefined;
  readonly columns: readonly TaxOverviewTableColumn[];
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly onEditClick: () => void;
};

export function TaxOverviewTableBodyRow({ rowFrag, columns, checked, onCheckedChange, onEditClick }: Props) {
  const { enableTaxFiling } = useFlags();
  const row = unmaskFragment(TaxOverviewTableBodyRowFragment, rowFrag);

  if (isNil(row)) {
    return null;
  }

  return (
    <>
      <TableCell className="flex items-center py-4 !pr-2 z-[8]" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          id={row.rowId}
          aria-label="Select"
          checked={checked}
          className="gap-0"
          onClick={(e) => {
            e.stopPropagation();
          }}
          onCheckedChange={(checkState) => {
            onCheckedChange(!!checkState);
          }}
        />
      </TableCell>
      {columns.map((column) => (
        <TableCell
          key={column}
          className={`flex items-center p-4 gap-2 ${column === 'Tax Status' ? '@container' : ''}`}
          style={{ ...getColumnSizing(column) }}
        >
          {renderColumnContent(column, row, !!enableTaxFiling)}
        </TableCell>
      ))}
      <TableCell className="flex items-center justify-center py-4 w-12">
        {!enableTaxFiling && (
          <IconButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          >
            <Ellipsis />
          </IconButton>
        )}
      </TableCell>
    </>
  );
}

function renderColumnContent(
  column: TaxOverviewTableColumn,
  row: ResultOf<typeof TaxOverviewTableBodyRowFragment>,
  taxFilingEnabled: boolean,
) {
  switch (column) {
    case 'Client':
      return <p>{row.enrollmentType === 'savvy_joint' ? row.householdName : row.clientName}</p>;
    case 'Filing Type':
      return (
        <p>{row.enrollmentType !== null ? formatTaxEnrollment(castToTaxEnrollmentType(row.enrollmentType)) : '-'}</p>
      );
    case 'Tax Year':
      return <p>{row.taxYear ?? '-'}</p>;
    case 'Tax Status':
      return (
        <TaxStatusBadge
          enrollmentStatus={row.enrollmentStatus}
          enrollmentType={row.enrollmentType}
          transcriptStatus={row.transcriptStatus}
          federalReturnStatus={row.federalReturnStatus}
          aprilAppStatus={row.aprilAppStatus}
          stateFilings={row.stateFilings}
          taxFilingEnabled={taxFilingEnabled}
        />
      );
    case 'Tax Price':
      return <p>{row.price !== null ? formatDollarAmount(row.price, true) : '-'}</p>;
    default:
      return null;
  }
}
